import React, { useState } from 'react';
import { Search, TrendingUp, Shield, Users, Star, ArrowRight } from 'lucide-react';
import { useProperty } from '../context/PropertyContext';
import PropertyCard from '../components/common/PropertyCard';
import Button from '../components/common/Button';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const HomePage: React.FC = () => {
  const { properties } = useProperty();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const displayedProperties = properties;

  const handlePropertyClick = (id: string) => {
    navigate(`/property/${id}`);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    navigate(`/search${params.toString() ? `?${params.toString()}` : ''}`);
  };

  // Home page content
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center min-h-[90vh] flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(35, 31, 32, 0.75), rgba(35, 31, 32, 0.75)), url(https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg)',
          backgroundPosition: 'center 40%'
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <img
              src="/stanorama-logo-negative.svg"
              alt="Stanorama"
              className="h-16 md:h-20 lg:h-24 w-auto mx-auto mb-10 animate-fade-in-up"
            />
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-10 text-white animate-fade-in-up">
              {t('home.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-12 max-w-3xl mx-auto animate-fade-in-up animation-delay-300 text-white">
              {t('home.hero.subtitle')}
            </p>
            
            {/* Enhanced Search Bar */}
            <form onSubmit={handleSearch} className="w-full max-w-4xl mx-auto animate-fade-in-up animation-delay-600">
              <div className="bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-large">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('home.searchPlaceholder')}
                      className="w-full px-6 py-4 text-lg rounded-xl border-0 focus:ring-2 focus:ring-primary-500 text-neutral-900 placeholder-neutral-500 bg-neutral-50 shadow-soft"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    size="lg"
                    className="lg:px-8 py-4"
                    icon={<Search size={20} />}
                  >
                    {t('home.search.button')}
                  </Button>
                </div>
              </div>
            </form>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-16 animate-fade-in-up animation-delay-900">
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold mb-2">{properties.length}+</div>
                <div className="text-white/80">{t('home.stats.properties')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold mb-2">50+</div>
                <div className="text-white/80">{t('home.stats.cities')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold mb-2">1000+</div>
                <div className="text-white/80">{t('home.stats.clients')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold mb-2">99%</div>
                <div className="text-white/80">{t('home.stats.satisfaction')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Properties Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
              {t('home.featured.title')}
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {t('home.search.premium')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {displayedProperties.slice(0, 3).map((property, index) => (
              <div key={property.id} className={`animate-fade-in-up animation-delay-${index * 150}`}>
                <PropertyCard
                  property={property}
                  onClick={() => handlePropertyClick(property.id)}
                  variant="default"
                />
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/search')}
              icon={<ArrowRight size={20} />}
              iconPosition="right"
            >
              {t('common.viewAll')}
            </Button>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
              {t('home.howItWorks.title')}
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {t('home.howItWorks.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Search size={32} />,
                title: t('home.howItWorks.step1.title'),
                description: t('home.howItWorks.step1.description'),
                color: 'primary'
              },
              {
                icon: <TrendingUp size={32} />,
                title: t('home.howItWorks.step2.title'),
                description: t('home.howItWorks.step2.description'),
                color: 'secondary'
              },
              {
                icon: <Shield size={32} />,
                title: t('home.howItWorks.step3.title'),
                description: t('home.howItWorks.step3.description'),
                color: 'accent'
              }
            ].map((step, index) => (
              <div 
                key={index} 
                className={`card p-8 text-center hover-lift hover-glow animate-fade-in-up animation-delay-${index * 200}`}
              >
                <div className={`w-20 h-20 ${
                  step.color === 'secondary' ? 'bg-brand-dark' : 'bg-primary-500'
                } rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-medium`}>
                  <div className="text-white">
                    {step.icon}
                  </div>
                </div>
                <div className={`text-2xl font-bold mb-2 ${
                  step.color === 'secondary' ? 'text-brand-dark' : 'text-primary-700'
                }`}>
                  {index + 1}
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-4">{step.title}</h3>
                <p className="text-neutral-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Recent Properties Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12">
            <div className="mb-6 lg:mb-0">
              <h2 className="text-4xl font-bold text-neutral-900 mb-4">{t('home.recent.title')}</h2>
              <p className="text-xl text-neutral-600">{t('home.search.recentlyAdded')}</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/search')}
              icon={<ArrowRight size={18} />}
              iconPosition="right"
            >
              {t('common.viewAll')}
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayedProperties.slice(0, 4).map((property, index) => (
              <div key={property.id} className={`animate-fade-in-up animation-delay-${index * 150}`}>
                <PropertyCard
                  property={property}
                  onClick={() => handlePropertyClick(property.id)}
                  variant="compact"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-neutral-900 mb-6">
                {t('home.features.whyChoose')}
              </h2>
              <div className="space-y-6">
                {[
                  {
                    icon: <Shield size={24} />,
                    title: t('home.features.secureTitle'),
                    description: t('home.features.secureDesc')
                  },
                  {
                    icon: <TrendingUp size={24} />,
                    title: t('home.features.smartTitle'),
                    description: t('home.features.smartDesc')
                  },
                  {
                    icon: <Users size={24} />,
                    title: t('home.features.expertTitle'),
                    description: t('home.features.expertDesc')
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <div className="text-primary-600">
                        {feature.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-neutral-900 mb-2">{feature.title}</h3>
                      <p className="text-neutral-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl bg-brand-dark p-8 shadow-large">
                <div className="h-full bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <div className="text-center text-white">
                    <Star size={64} className="mx-auto mb-4" />
                    <div className="text-4xl font-bold mb-2">4.9/5</div>
                    <div className="text-lg">{t('home.features.rating')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-brand-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">{t('home.cta.title')}</h2>
          <p className="text-xl lg:text-2xl mb-12 max-w-3xl mx-auto text-white/90">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <Button 
              onClick={() => navigate('/register')} 
              variant="primary"
              size="xl"
              className="bg-neutral-900 text-white hover:bg-neutral-800"
            >
              {t('common.signUpNow')}
            </Button>
            <Button 
              onClick={() => navigate('/search')} 
              variant="outline"
              size="xl"
              className="border-white text-white hover:bg-white/10"
            >
              {t('common.browseProperties')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
